import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import type { InputHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/lib/format";

/**
 * The primitives every screen is built from, on the Modernist design system
 * (.docs/08 §2).
 *
 * The system itself lives in `src/styles/modernist.css` and is the source of
 * truth for the look; this file is the React surface over it. So the rule here
 * is: reach for a system class (`.btn`, `.input`, `.tag`, `.table`) and use
 * Tailwind only for layout. A component that restyles what the system already
 * styles is a component that will drift from it.
 *
 * The seven components P5 shipped keep their exact signatures — eleven screens
 * import them and F6 is where those screens get rewritten, not here. What
 * changed is what they render.
 */

/* ── Actions ───────────────────────────────────────────────────────────── */

type ButtonVariant = "primary" | "secondary" | "ghost";

const BUTTON_CLASS: Record<ButtonVariant, string> = {
  primary: "btn btn-primary",
  secondary: "btn btn-secondary",
  ghost: "btn btn-ghost",
};

export function Button({
  variant = "secondary",
  block = false,
  className = "",
  type = "button",
  ...props
}: {
  variant?: ButtonVariant;
  /** Full width, for the stacked action rows a phone forces (.docs/08 §6). */
  block?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      // Never `type="submit"` by default. A bare <button> inside a <form>
      // submits it, which is how a "Cancel" ends up saving the record.
      type={type}
      className={`${BUTTON_CLASS[variant]}${block ? " btn-block justify-center" : ""} ${className}`.trim()}
      {...props}
    />
  );
}

/* ── Form controls ─────────────────────────────────────────────────────── */

export function Input({
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input ${className}`.trim()} {...props} />;
}

export function Select({
  className = "",
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`input ${className}`.trim()} {...props} />;
}

export function Textarea({
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`input ${className}`.trim()} {...props} />;
}

/**
 * A labelled form control.
 *
 * Not `Field` — that name was taken by the definition-list row below, which
 * eleven screens already import and which is a different thing entirely: one
 * reads a value, this one writes it. Renaming either would be a rename across
 * the app for no gain.
 *
 * The label is wired to the control by a generated id rather than by wrapping
 * it, because the system styles `.field > label` as a block above the input and
 * a wrapping label would put the control inside its own caption.
 */
export function FormField({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: ReactNode;
  /** The line under the control. Use it for what a seller cannot guess. */
  hint?: ReactNode;
  /** Pass when the caller owns the control's id; otherwise one is generated. */
  htmlFor?: string;
  children: (id: string) => ReactNode;
}) {
  const generated = useId();
  const id = htmlFor ?? generated;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint ? <div className="mt-1.5 text-[11px] text-mute-600">{hint}</div> : null}
    </div>
  );
}

/**
 * The segmented control — the system's `.seg`, driven by radios.
 *
 * Radios rather than buttons because that is what it is: one choice from a
 * small closed set, and the native control brings arrow-key navigation and
 * the grouping a screen reader announces.
 */
export function Seg<T extends string>({
  name,
  value,
  options,
  onChange,
  className = "",
}: {
  name: string;
  value: T;
  options: readonly { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div className={`seg ${className}`.trim()}>
      {options.map((option) => (
        <label className="seg-opt" key={option.value}>
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

/* ── Layout ────────────────────────────────────────────────────────────── */

export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-[1440px] p-4 sm:p-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          {subtitle ? (
            // The kicker sits *above* the title in Modernist, not below it as a
            // subtitle: it is the count or the period, and the heading is what
            // the screen is (.docs/08 §2).
            //
            // A paragraph, not an `h6`. It reads as a heading and is not one —
            // marking it up as one puts the company's own name in the document
            // outline above the `h1` that names the page, and every "find the
            // heading" query then matches two elements.
            <p className="kicker mb-1.5 text-brand-700">{subtitle}</p>
          ) : null}
          <h1 className="text-[30px] sm:text-[40px]">{title}</h1>
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-3">{actions}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-2 border-rule bg-ground">
      {title ? (
        <header className="flex items-center justify-between gap-4 border-b-2 border-rule px-4 py-3">
          <h4 className="text-[17px]">{title}</h4>
          {actions}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/**
 * A data table. The wrapper is not decoration: a wide table has to scroll
 * inside its own box, or the whole page scrolls sideways and the header goes
 * with it (.docs/08 §6).
 */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="table">{children}</table>
    </div>
  );
}

/* ── Status and labels ─────────────────────────────────────────────────── */

type Tone = "accent" | "neutral" | "outline";

export function Tag({
  tone = "neutral",
  className = "",
  title,
  children,
}: {
  tone?: Tone;
  className?: string;
  /** Hover text. The design uses it to explain a manager-only affordance. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`tag tag-${tone} ${className}`.trim()} title={title}>
      {children}
    </span>
  );
}

/**
 * The nine company statuses in the system's three tones (D21).
 *
 * Keyed to the legacy integer, never to the label: the labels are Spanish
 * prose that will get reworded, the integers are legacy values that must never
 * be renumbered. That was true of the six-hue mapping this replaces and is the
 * one property that had to survive the collapse to a mono palette.
 *
 *   accent  → money in play  · Interesado, Contratado, Requerimiento
 *   outline → needs action   · Hablar después, Por renovar
 *   neutral → closed         · Cancelado, No interesado, Por ahora no, Duplicada
 */
const STATUS_TONES: Record<number, Tone> = {
  1: "accent", // Interesado
  2: "outline", // Hablar después
  4: "accent", // Contratado
  5: "neutral", // Cancelado
  6: "neutral", // No interesado
  7: "neutral", // Por ahora no
  8: "outline", // Por renovar
  9: "neutral", // Duplicada
  10: "accent", // Requerimiento
};

export function StatusBadge({
  status,
  label,
}: {
  status: number | undefined;
  label: string;
}) {
  const tone = (status === undefined ? undefined : STATUS_TONES[status]) ?? "neutral";
  return <span className={`tag tag-${tone} shrink-0`}>{label}</span>;
}

/* ── States ────────────────────────────────────────────────────────────── */

export function Spinner({ label }: { label: string }) {
  return (
    <div className="p-8 text-center text-sm text-mute-600" role="status">
      {label}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="p-8 text-center text-sm text-mute-600">{children}</div>;
}

/**
 * Never a blank panel. A failed fetch that renders as "no results" is the bug
 * that makes a seller believe their book of business disappeared.
 */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="p-8 text-center">
      <p className="text-sm text-brand-700">{message}</p>
      {onRetry ? (
        <Button className="mt-3" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * A section whose data has no model yet (.docs/08 §5, D22).
 *
 * Deliberately not a zero, not a spinner, and not a hidden section. A zero is
 * data and it would be false — a seller who sees $0 next to "Venta total del
 * mes" concludes they sold nothing. A permanent spinner reads as a broken app.
 * Hiding it means designing the layout twice, once now and once when the model
 * arrives.
 *
 * Reserving the space is the entire point, so give it the height the real
 * content will take: that is what stops the panel jumping on the day it is
 * connected. The dashed edge reads as "not yet" precisely because nothing else
 * in a system built from solid 2px rules is dashed.
 */
export function Reserved({
  label,
  children,
  className = "",
}: {
  /** What belongs here, in the words the connected version will use. */
  label: ReactNode;
  /** One line on what is missing. */
  children: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex flex-col gap-1.5 border-2 border-dashed border-mute-400 bg-panel px-5 py-5 ${className}`.trim()}
    >
      <span className="font-display text-[10px] font-extrabold tracking-[0.1em] text-mute-600 uppercase">
        {label}
      </span>
      <span className="font-display text-[19px] font-extrabold text-mute-400">
        {t("reserved.title")}
      </span>
      <span className="max-w-[44ch] text-xs text-mute-600">{children}</span>
    </div>
  );
}

/**
 * One row of a definition list — a label and its value, on a detail screen.
 * Reads a value; `FormField` writes one.
 */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-6 border-b border-rule px-4 py-2 text-sm last:border-0">
      <dt className="shrink-0 text-mute-600">{label}</dt>
      <dd className="text-right break-words">{children}</dd>
    </div>
  );
}

/**
 * "creado el 27 ago 2026, 09:00 a.m. por Melissa Parada" — when a record was
 * written and by whom. A contact and a company carry it, the way a follow-up
 * does on the timeline.
 *
 * `by` is `created_by_agent`, who typed the row, and never `current_agent`,
 * who holds it now. It is null for a row nobody typed — a legacy import whose
 * author is gone, a lead a webhook opened — and then only the date is said.
 */
export function CreatedBy({
  created,
  by,
  className = "",
}: {
  created: string;
  by: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const when = formatDateTime(created);
  return (
    <p className={`text-xs text-mute-700 ${className}`.trim()}>
      {by ? t("common.createdBy", { when, agent: by }) : t("common.created", { when })}
    </p>
  );
}
